import { rem } from 'polished'
import React, { useCallback } from 'react'
import styled from 'styled-components'
import TextButton from 'LuxKit/Button/TextButton'
import LineTrashAltIcon from 'LuxKit/Icons/line/LineTrashAltIcon'
import zIndex from 'styles/tools/z-index'
import LinePlusIcon from 'LuxKit/Icons/line/LinePlusIcon'
import { tileDesktopLayout } from './constants'
import Group from 'components/utils/Group'
import Counter, { CounterChangeEventHandler } from 'LuxKit/Counter'
import BodyText from 'LuxKit/Typography/BodyText'
import MerchandiseMaxQuantityMessage from 'components/Merchandise/MerchandiseMaxQuantityMessage'

const MessageRow = styled.div`
  /* On mobile: visible, full width */
  width: 100%;
  display: flex;
  justify-content: flex-end;

  ${tileDesktopLayout} {
    /* On desktop: hidden, message shown in CounterAndMessageGroup instead */
    display: none;
  }
`

// Can't use <Group /> as our 'breakpoints' are container queries, not screen sizes
const ActionsContainer = styled.div`
  display: flex;
  justify-content: start;
  gap: ${rem(8)};
  flex-wrap: wrap;

  ${tileDesktopLayout} {
    flex-direction: column;
    justify-content: end;
    flex-wrap: nowrap;
  }
`

const ActionsRow = styled.div`
  z-index: ${zIndex.reviewCartButtons};
  display: flex;
  gap: ${rem(8)};
  width: 100%;
  justify-content: space-between;
  align-items: center;

  ${tileDesktopLayout} {
    width: auto;
    justify-content: end;
  }
`

const ButtonsGroup = styled.div`
  display: flex;
  gap: ${rem(8)};

  ${tileDesktopLayout} {
    justify-content: end;
  }
`

const CounterAndMessageGroup = styled.div`
  /* On mobile: hidden, counter is in ActionsRow instead */
  display: none;

  ${tileDesktopLayout} {
    /* On desktop: shown as flex column */
    display: flex;
    flex-direction: column;
    gap: ${rem(4)};
  }
`

const MobileCounterGroup = styled(Group)`
  /* On mobile: visible in ActionsRow */
  display: flex;

  ${tileDesktopLayout} {
    /* On desktop: hidden, shown in CounterAndMessageGroup instead */
    display: none;
  }
`

const DesktopCounterGroup = styled(Group)`
  /* On mobile: hidden */
  display: none;

  ${tileDesktopLayout} {
    /* On desktop: visible */
    display: flex;
  }
`

interface Props {
  variant: App.MerchandiseOfferVariant
  itemCount: number
  saveForLater?: boolean
  onSaveForLater?: React.MouseEventHandler<HTMLButtonElement>
  onDelete?: React.MouseEventHandler<HTMLButtonElement>
  onAddItem?: () => void
  onRemoveItem?: () => void
}

function MerchandiseCartReviewTileActions({
  variant,
  itemCount,
  saveForLater,
  onSaveForLater,
  onDelete,
  onAddItem,
  onRemoveItem,
}: Props) {
  const isBookable = !!variant.inventory

  const onQuantityCounterChange = useCallback<CounterChangeEventHandler>((value, _, e) => {
    e.preventDefault()
    if (value > itemCount) {
      onAddItem?.()
    } else {
      onRemoveItem?.()
    }
  }, [onAddItem, onRemoveItem, itemCount])

  const isMaxQuantityReached = itemCount >= (variant.inventory ?? 0) && !!variant.inventory

  const counterContent = <>
    <BodyText variant="small" weight="bold">Qty</BodyText>
    <Counter
      value={itemCount}
      onChange={onQuantityCounterChange}
      min={1}
      max={variant.inventory}
      size="small"
    />
  </>

  const messageContent = isMaxQuantityReached ? <MerchandiseMaxQuantityMessage /> : null

  return <ActionsContainer>
    <ActionsRow>
      <ButtonsGroup>
        {isBookable && <>
          {!saveForLater && <TextButton
            kind="secondary"
            size="small"
            shape="round"
            onClick={onSaveForLater}
          >
            Save for later
          </TextButton>}
          {saveForLater && <TextButton
            kind="primary"
            size="small"
            shape="round"
            startIcon={<LinePlusIcon />}
            onClick={onSaveForLater}
          >
            Add
          </TextButton>}
        </>}
        <TextButton
          kind="secondary"
          size="small"
          shape="round"
          startIcon={<LineTrashAltIcon />}
          onClick={onDelete}
        >
          Delete
        </TextButton>
      </ButtonsGroup>
      {!!isBookable && <MobileCounterGroup
        direction="horizontal"
        gap={8}
        verticalAlign="center"
        horizontalAlign="end"
      >
        {counterContent}
      </MobileCounterGroup>}
    </ActionsRow>
    {!!isBookable && <CounterAndMessageGroup>
      <DesktopCounterGroup
        direction="horizontal"
        gap={8}
        verticalAlign="center"
        horizontalAlign="end"
      >
        {counterContent}
      </DesktopCounterGroup>
      {messageContent}
    </CounterAndMessageGroup>}
    <MessageRow>
      {messageContent}
    </MessageRow>
  </ActionsContainer>
}

export default MerchandiseCartReviewTileActions
